
import { useCallback } from 'react';
import { useThrottlingDebounce } from './useThrottlingDebounce';
import { useFetchOperation } from './useFetchOperation';
import { useLoadingState } from './useLoadingState';
import { Subscription } from '../types';
import { useToast } from '@/hooks/use-toast';

interface UseSubscriptionFetchLogicProps {
  userId: string | undefined;
  setIsLoading: (isLoading: boolean) => void;
  setIsSubscribed: (isSubscribed: boolean) => void;
  setCurrentSubscription: (subscription: Subscription | null) => void;
  setLastChecked: (date: Date | null) => void;
  setIsRetrying: (isRetrying: boolean) => void;
  isRefreshingRef: React.MutableRefObject<boolean>;
  isMountedRef: React.MutableRefObject<boolean>;
  fetchInProgressRef: React.MutableRefObject<boolean>;
  debounceTimeoutRef: React.MutableRefObject<number | null>;
  lastFetchTimeRef: React.MutableRefObject<number>;
}

export function useSubscriptionFetchLogic({
  userId,
  setIsLoading,
  setIsSubscribed,
  setCurrentSubscription,
  setLastChecked,
  setIsRetrying,
  isRefreshingRef,
  isMountedRef,
  fetchInProgressRef,
  debounceTimeoutRef,
  lastFetchTimeRef
}: UseSubscriptionFetchLogicProps) {
  const { toast } = useToast();
  
  const { resetLoadingState, startLoading } = useLoadingState({
    setIsLoading,
    setIsRetrying,
    isRefreshingRef,
    isMountedRef
  });
  
  const { fetchProfileSubscription, fetchUserSubscriptions } = useFetchOperation({
    userId,
    isMountedRef,
    setIsSubscribed,
    setCurrentSubscription,
    setLastChecked
  });
  
  const { 
    cleanupTimeouts: cleanupDebounceTimeouts,
    shouldThrottleRequest,
    isFetchInProgress,
    updateLastFetchTime
  } = useThrottlingDebounce({
    fetchInProgressRef,
    debounceTimeoutRef,
    lastFetchTimeRef,
  });

  const executeSubscriptionFetch = useCallback(async (showToast: boolean) => {
    if (!userId || !isMountedRef.current) {
      console.log('Skipping fetch - invalid state:', { 
        noUser: !userId, 
        unmounted: !isMountedRef.current
      });
      return false;
    }
    
    fetchInProgressRef.current = true;
    updateLastFetchTime();
    startLoading();
    
    try {
      console.log('Starting subscription status fetch for user:', userId);
      
      // First check the profile subscription tier - this is authoritative
      const profileData = await fetchProfileSubscription();
      
      if (!profileData || !isMountedRef.current) {
        console.log('Failed to fetch profile data or component unmounted');
        resetLoadingState();
        fetchInProgressRef.current = false;
        return false;
      }
      
      const { isProTier } = profileData;
      console.log('=== SUBSCRIPTION TIER DETERMINATION ===');
      console.log('Profile subscription check - Is Pro tier:', isProTier);
      
      // Try to fetch subscription details for additional info (but don't let it override the profile check)
      let subscription = null;
      if (isProTier) {
        subscription = await fetchUserSubscriptions(userId);
        if (isMountedRef.current) {
          setCurrentSubscription(subscription);
          console.log('Set current subscription:', subscription);
        }
      } else {
        if (isMountedRef.current) {
          setCurrentSubscription(null);
          console.log('Cleared current subscription (not Pro tier)');
        }
      }
      
      if (isMountedRef.current) {
        setLastChecked(new Date());
        
        if (showToast) {
          toast({
            title: "Subscription refreshed",
            description: `Status: ${isProTier ? 'Pro' : 'Free'}`,
          });
        }
      }
      
      console.log('=== SUBSCRIPTION FETCH COMPLETED ===');
      console.log('Final subscription status set to:', isProTier);
      resetLoadingState();
      fetchInProgressRef.current = false;
      return true;
      
    } catch (err) {
      console.error('Error fetching subscription status:', err);
      
      if (isMountedRef.current && showToast) {
        toast({
          title: "Failed to refresh subscription",
          description: "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
      
      resetLoadingState();
      fetchInProgressRef.current = false;
      return false;
    }
  }, [
    userId,
    toast,
    isMountedRef,
    fetchInProgressRef,
    updateLastFetchTime,
    startLoading,
    fetchProfileSubscription,
    resetLoadingState,
    setIsSubscribed,
    fetchUserSubscriptions,
    setCurrentSubscription,
    setLastChecked
  ]);

  return {
    executeSubscriptionFetch,
    cleanupDebounceTimeouts,
    shouldThrottleRequest,
    isFetchInProgress
  };
}
